import React, { useState, useEffect, useRef } from "react";
import { TextField, IconButton } from "@material-ui/core";
import { Send as SendIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@material-ui/icons";
import { useMobileDetection } from "../../utils/deviceDetection";
import PlayerAvatar from "./PlayerAvatar";
import "../../styling/game-chat.css";

const GameChat = ({ socket, room, name, playerColors, playerEmojis, messages = [], showHeader = true }) => {
  const [inputMessage, setInputMessage] = useState("");
  const [expanded, setExpanded] = useState(true);
  const isMobile = useMobileDetection();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (expanded || isMobile || !showHeader) {
      scrollToBottom();
    }
  }, [messages, expanded, isMobile, showHeader]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !socket?.current) return;

    socket.current.emit("lobby_chat_message", {
      room,
      name,
      message: inputMessage.trim()
    });

    setInputMessage("");
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const showContent = expanded || isMobile || !showHeader;

  return (
    <div className={`game-chat-container ${expanded ? 'game-chat-expanded' : 'game-chat-collapsed'}`}>
      {showHeader && (
        <div className="game-chat-header" onClick={toggleExpanded}>
          <h3>💬 Chat</h3>
          <IconButton size="small" className="game-chat-toggle">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </div>
      )}
      {showContent && (
        <div className="game-chat-content">
          <div className="game-chat-messages" ref={chatContainerRef}>
            {messages.length === 0 ? (
              <div className="game-chat-empty">
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`game-chat-message ${
                    msg.name === name ? "game-chat-message-own" : ""
                  }`}
                >
                  <div className="game-chat-message-avatar">
                    <PlayerAvatar
                      playerName={msg.name}
                      playerColor={playerColors[msg.name]}
                      playerEmoji={playerEmojis[msg.name]}
                      size="small"
                      showLabel={false}
                    />
                  </div>
                  <div className="game-chat-message-content">
                    <div className="game-chat-message-header">
                      <span className="game-chat-message-name">{msg.name}</span>
                      <span className="game-chat-message-time">
                        {msg.timestamp
                          ? new Date(msg.timestamp).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit"
                            })
                          : ""}
                      </span>
                    </div>
                    <div className="game-chat-message-text">{msg.message}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          <form className="game-chat-input-form" onSubmit={handleSendMessage}>
            <TextField
              fullWidth
              placeholder="Type a message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              variant="outlined"
              size="small"
              className="game-chat-input"
              disabled={!socket?.current}
            />
            <IconButton
              type="submit"
              color="primary"
              disabled={!inputMessage.trim() || !socket?.current}
              className="game-chat-send-button"
            >
              <SendIcon />
            </IconButton>
          </form>
        </div>
      )}
    </div>
  );
};

export default GameChat;

